import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '@/components/Logo';
import { useAuth } from '@/services/auth/authContext';
import { useToast } from '@/components/ui/use-toast';
import SEOHead from '@/components/SEOHead';
import Cookies from 'js-cookie';

const RestaurantLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check if already logged in and redirect
  useEffect(() => {
    const checkExistingAuth = async () => {
      const token = Cookies.get('auth_token');
      const isDemo = localStorage.getItem('isDemo') === 'true';

      if (token && !isDemo) {
        // User has valid auth token, redirect to dashboard
        try {
          const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://dineai.ai/api'}/restaurants/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            toast({
              title: "Already logged in",
              description: "Redirecting to your dashboard...",
            });
            navigate('/restaurant-dashboard', { replace: true });
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
    };

    checkExistingAuth();
  }, [navigate, toast]);

  // Add keyboard shortcut for demo access
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      // Check for Ctrl+Shift+D (Windows/Linux) or Cmd+Shift+D (Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        navigate('/dancing-queen');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const handleSocialLogin = async (provider: string) => {
    try {
      setIsLoading(true);
      // Pass false for isNewUser since this is the login page for existing users
      await login(provider, false);
      toast({
        title: "Welcome back!",
        description: "Redirecting to your dashboard...",
      });
    } catch (error) {
      toast({
        title: "Login failed",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <>
      <SEOHead 
        title="Restaurant Login | Dine AI Dashboard Access"
        description="Access your Dine AI restaurant dashboard. Monitor analytics, manage menus, and track customer insights. Secure login for restaurant owners."
        keywords="restaurant login, dine ai dashboard, restaurant management portal"
      />
      <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-dineai-yellow/10 blur-3xl"></div>
      </div>

      {/* Logo in top-left corner - clickable to home */}
      <Link to="/" className="absolute top-8 left-8 z-20 hover:opacity-80 transition-opacity">
        <Logo className="h-10" />
      </Link>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen pt-20 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >

          {/* Login Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 bg-clip-text text-transparent">
                Welcome back!!
              </h1>
              <p className="text-white/80 text-lg mb-6">
                Sign in to access your AI-powered insights
              </p>

              {/* Social Proof Badge - Elegant Pill */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-[#FDB913]" />
                <span className="text-[#FDB913] font-medium text-sm">Join 1,000+ restaurants</span>
              </div>
            </div>

            {/* Social Login Buttons - Full Width Design */}
            <div className="space-y-4">
              {/* Google Login */}
              <button
                onClick={() => handleSocialLogin('google')}
                disabled={isLoading}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* Apple Login */}
              <button
                onClick={() => handleSocialLogin('apple')}
                disabled={isLoading}
                className="w-full bg-black hover:bg-gray-900 text-white font-semibold py-4 px-6 rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.05 20.28c-.98.95-2.05.96-3.08.44-1.07-.54-2.13-.52-3.25 0-1.44.73-2.21.53-3.1-.44C2.32 14.85 3.07 6.67 8.28 6.31c1.66.07 2.81 1.12 3.72 1.12.89 0 2.37-1.38 4.28-1.17 1.58.12 2.78.73 3.58 1.86-3.25 1.95-2.72 6.21.4 7.47-.95 2.06-2.18 4.1-3.21 4.69zM13.33 5.33c.86-1.1 1.51-2.62 1.28-4.2-1.4.09-3.03.95-3.98 2.13-.86 1.05-1.57 2.61-1.3 4.13 1.53.12 3.01-.83 4-2.06z" />
                </svg>
                Continue with Apple
              </button>
            </div>
          </div>

          {/* Trust Text */}
          <p className="text-center text-white/60 text-sm mt-6">
            Secure authentication • No passwords needed • Join the AI revolution
          </p>
        </motion.div>
      </div>
    </div>
    </>
  );
};

export default RestaurantLogin;